import { useEffect, useState } from "react";
import { motion, LayoutGroup, useReducedMotion } from "framer-motion";
import { LayoutDashboard, Users, KanbanSquare, BarChart3, Settings } from "lucide-react";
import { useLang } from "@/i18n/LangContext";

const statValues = [124, 38, 16, 12];
const colCounts = [5, 3, 2, 1, 1];

export default function CrmMockup() {
  const { t } = useLang();
  const reduce = useReducedMotion();
  const stats = t("crm.stats");
  const pipeline = t("crm.pipeline");
  const [col, setCol] = useState(0);

  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => setCol((c) => (c + 1) % 5), 2000);
    return () => clearInterval(id);
  }, [reduce]);

  return (
    <div data-testid="crm-mockup" className="overflow-hidden rounded-2xl border border-ink/10 bg-white shadow-[0_30px_80px_-30px_rgba(7,17,31,0.35)]">
      <div className="flex items-center gap-1.5 border-b border-ink/10 bg-mist px-4 py-2.5">
        <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
        <span className="ml-3 rounded-full border border-ink/10 bg-white px-3 py-0.5 text-[11px] text-slate-500">crm.digital-company.gr</span>
      </div>
      <div className="flex">
        <div className="hidden sm:flex w-14 flex-col items-center gap-5 border-r border-ink/10 bg-mist/60 py-5 text-slate-400">
          <LayoutDashboard className="h-5 w-5 text-dblue" />
          <Users className="h-5 w-5" />
          <KanbanSquare className="h-5 w-5" />
          <BarChart3 className="h-5 w-5" />
          <Settings className="h-5 w-5 mt-auto" />
        </div>
        <div className="flex-1 p-4 sm:p-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {stats.map((label, i) => (
              <div key={label} className="rounded-xl border border-ink/10 bg-mist/60 p-3.5">
                <p className="text-[9px] font-bold tracking-[0.18em] text-slate-500">{label}</p>
                <p className="mt-1.5 font-display text-2xl font-extrabold text-ink">{statValues[i]}</p>
              </div>
            ))}
          </div>
          <LayoutGroup>
            <div className="mt-4 grid grid-cols-5 gap-2">
              {pipeline.map((stage, ci) => (
                <div key={stage} className="rounded-lg border border-ink/10 bg-mist/40 p-2 min-h-[120px]">
                  <div className="flex items-center justify-between">
                    <p className="text-[8px] sm:text-[9px] font-bold tracking-wider text-slate-500">{stage}</p>
                    <span className="text-[9px] font-bold text-dblue">{colCounts[ci] + (col === ci ? 1 : 0)}</span>
                  </div>
                  <div className="mt-2 space-y-1.5">
                    {Array.from({ length: Math.min(colCounts[ci], 2) }).map((_, k) => (
                      <div key={k} className="rounded-md border border-ink/10 bg-white p-1.5">
                        <div className="h-1.5 w-3/4 rounded bg-slate-200" />
                        <div className="mt-1 h-1.5 w-1/2 rounded bg-slate-100" />
                      </div>
                    ))}
                    {col === ci && (
                      <motion.div
                        layoutId="moving-lead"
                        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                        className={`rounded-md border p-1.5 ${ci === 4 ? "border-emerald-400 bg-emerald-50" : "border-dblue/50 bg-blue-50"}`}
                      >
                        <p className="text-[8px] font-bold text-ink">ABC INDUSTRIES</p>
                        <div className="mt-1 h-1.5 w-2/3 rounded bg-dblue/30" />
                      </motion.div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </LayoutGroup>
        </div>
      </div>
      <div className="border-t border-ink/10 bg-mist/60 px-4 py-2">
        <p className="text-[10px] text-slate-500 italic">{t("crm.demo")}</p>
      </div>
    </div>
  );
}
